/**
 * Siapa boleh melihat tombol apa pada antrean koreksi — MURNI.
 *
 * Sama seperti `navigation.ts`: ini menentukan apa yang DITAMPILKAN, bukan apa yang DIIZINKAN.
 * Penegakan ada di API (`RequirePermission` + `SelfApprovalNotAllowedError`/ATT-009). Menghapus
 * modul ini tidak memberi siapa pun kuasa memutuskan pengajuan yang seharusnya ditolak — ia
 * hanya membuat orang menekan tombol yang berakhir dengan penolakan.
 *
 * Alasan modul ini tetap ada: menekan "Setujui" lalu ditolak ATT-009 terasa seperti sistem
 * rusak, padahal aturannya memang benar. Lebih baik tombolnya tidak pernah muncul, dengan
 * keterangan mengapa.
 */

import { formatHalfDayPart, formatLeaveType } from './correction-form';
import { hasAnyPermission } from './navigation';

export interface CorrectionActor {
  readonly userId: string;
  readonly permissions: readonly string[];
}

export interface CorrectionLite {
  readonly status: string;
  readonly requestedByUserId: string;
  readonly subjectUserId: string;
}

export interface DecisionAbility {
  readonly canApprove: boolean;
  readonly canReject: boolean;
  readonly canCancel: boolean;
  /** Mengapa keputusan tidak tersedia — ditampilkan menggantikan tombol, bukan disembunyikan diam-diam. */
  readonly blockedReason: string | null;
}

/**
 * Aturan reviewer ≠ requester ≠ subject (ATTENDANCE-FLOW §5).
 *
 * Dua kondisi terpisah yang sengaja tidak digabung: seseorang bisa mengajukan koreksi UNTUK
 * orang lain (requester), dan bisa pula menjadi orang yang absensinya dikoreksi (subject).
 * Keduanya menghalangi, dan alasannya berbeda bagi pembaca.
 */
export function decisionAbility(
  actor: CorrectionActor,
  correction: CorrectionLite,
): DecisionAbility {
  const isOwnRequest = correction.requestedByUserId === actor.userId;
  const canCancel = isOwnRequest && correction.status === 'PENDING';

  if (correction.status !== 'PENDING') {
    return {
      canApprove: false,
      canReject: false,
      canCancel: false,
      blockedReason: 'Pengajuan ini sudah diputuskan.',
    };
  }

  if (isOwnRequest) {
    return {
      canApprove: false,
      canReject: false,
      canCancel,
      blockedReason: 'Anda pengaju koreksi ini, jadi tidak boleh ikut memutuskan.',
    };
  }

  if (correction.subjectUserId === actor.userId) {
    return {
      canApprove: false,
      canReject: false,
      canCancel: false,
      blockedReason: 'Koreksi ini atas absensi Anda sendiri, jadi tidak boleh Anda putuskan.',
    };
  }

  const canApprove = hasAnyPermission(actor.permissions, [
    'workforce.attendance.correction.approve',
  ]);
  const canReject = hasAnyPermission(actor.permissions, ['workforce.attendance.correction.reject']);

  return {
    canApprove,
    canReject,
    canCancel,
    blockedReason:
      canApprove || canReject ? null : 'Anda tidak diberi wewenang memutuskan koreksi absensi.',
  };
}

/**
 * Perbandingan sebelum → sesudah yang bisa dibaca manajer.
 *
 * `requestedPayload` adalah `jsonb` bebas dari server. Yang ditampilkan hanya kunci yang
 * dikenal; kunci asing tetap ditampilkan apa adanya alih-alih disembunyikan, karena manajer
 * yang memutuskan berhak melihat seluruh isi pengajuan.
 */
export interface ComparisonRow {
  readonly label: string;
  readonly before: string;
  readonly after: string;
}

const PAYLOAD_LABEL: Readonly<Record<string, string>> = {
  proposedTime: 'Jam',
  workMode: 'Mode kerja',
  leaveType: 'Jenis izin',
  part: 'Bagian hari',
};

/**
 * Nilai mentah tidak selalu layak dibaca apa adanya — `leaveType: 'SICK'` berarti sesuatu bagi
 * mesin, bukan bagi manajer yang memutuskan. Kunci lain di luar daftar ini tetap ditampilkan
 * mentah, konsisten dengan alasan `buildComparison` tidak menyaring kunci asing.
 */
const VALUE_FORMATTER: Readonly<Record<string, (raw: string) => string>> = {
  leaveType: formatLeaveType,
  part: formatHalfDayPart,
};

export function buildComparison(
  requestedPayload: Record<string, unknown>,
  current: Readonly<Record<string, string | null>>,
): readonly ComparisonRow[] {
  return Object.entries(requestedPayload).map(([key, value]) => {
    const mentah = value === null || value === undefined ? null : String(value);
    const format = VALUE_FORMATTER[key];
    return {
      label: PAYLOAD_LABEL[key] ?? key,
      before: current[key] ?? 'Tidak ada catatan',
      after: mentah === null ? '—' : format ? format(mentah) : mentah,
    };
  });
}
